import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { DEFAULT_SORT, nextSort, sortEntries, type DocSort, type SortKey } from '@/pages/docs/docSort';
import { nasConnectionsOfScope, type NasScope } from '@/pages/docs/NasManager';
import { officeFormatOf } from '@/pages/docs/office/officeFormats';
import { openOfficeWindow } from '@/pages/docs/office/useOfficeEditor';
import {
  nasDownloadUrl,
  useBrowsableNasConnections,
  useNasBrowse,
  useNasSearch,
  useS3Browse,
  type DocumentEntry,
  type DocumentListing,
} from '@/pages/docs/useDocuments';
import { useS3Search } from '@/pages/docs/useFolderShares';
import { viewerKindOf } from '@/pages/docs/viewerFormats';
import { docsViewerHref } from '@/pages/docs/viewer/viewerLink';

import { setDocDrag } from './docDrag';

/**
 * 자료실 우측 도크 — <b>다른 화면을 보면서 자료를 함께 본다</b>(사용자 지시 2026-09-04).
 *
 * <h2>왜 도크인가</h2>
 * 현장 정보을 보다가·공지를 쓰다가 «그 도면이 어디 있더라» 를 확인하려면 지금은 자료실로 화면을
 * <b>옮겨야</b> 하고, 돌아오면 보던 자리를 다시 찾아야 한다. AI Agent 가 같은 이유로 도크를 갖고
 * 있고({@link AiAgentDock}), 이 도크는 그 옆에 나란히 선다.
 *
 * <p>도크는 {@code Outlet} <b>밖</b>에 산다({@link AppLayout}) — 화면을 옮겨도 열어 둔 자료가 그대로
 * 남아야 «보면서» 가 성립한다. 폴더 위치를 주소에 두지 않는 이유도 같다: 뒤 화면이 이미 쓰고 있는
 * 칸({@code ?path=} 따위)과 부딪친다.
 *
 * <h2>여기서 하는 일은 셋뿐이다 — 보기·찾기·끌기</h2>
 * 420px 안에 자료실 화면을 밀어 넣지 않는다(사용자 결정 2026-09-04). 새 폴더·공유·옮기기·AI 학습
 * 대기열은 자료실 화면의 일이다 — 좁은 칸에 단추를 빼곡히 세우면 정작 <b>파일을 찾는 일</b>이 밀린다.
 * 파일을 여는 것은 <b>새 창</b>이다: 같은 창으로 열면 보고 있던 화면이 덮여, 도크를 만든 이유가 사라진다.
 *
 * <h2>끌어다 놓으면 «참조» 가 된다</h2>
 * 목록의 파일은 전부 끌 수 있다({@code docDrag}). 받는 자리는 AI 대화창·현장 서류 탭·글쓰기
 * 첨부칸이고, 옮겨 가는 것은 파일이 아니라 <b>그 자료를 가리키는 말</b>이다.
 */
export function StorageDock({
  open,
  onClose,
  offsetRight = 0,
}: {
  open: boolean;
  onClose: () => void;
  /**
   * 오른쪽 끝에서 이만큼 떨어져 선다. AI 도크가 함께 열려 있으면 그 폭이 들어온다 —
   * 둘 다 오른쪽 끝에 붙으면 <b>겹쳐서</b> 뒤엣것이 통째로 가려진다.
   */
  offsetRight?: number;
}) {
  const navigate = useNavigate();

  /** 어느 저장소를 보고 있나. 자료실 홈의 카드 셋과 같은 갈래다. */
  const [source, setSource] = useState<'S3' | 'HQ_NAS' | 'SITE_NAS'>('S3');
  /** S3 프리픽스(끝에 `/`) 또는 NAS 상대경로 — 갈래마다 표기 규칙이 달라 따로 둔다. */
  const [prefix, setPrefix] = useState('');
  const [path, setPath] = useState('');
  const [connectionId, setConnectionId] = useState<string | null>(null);

  /** 치고 있는 말과 <b>실제로 보낸</b> 말을 나눈다 — NAS 검색은 한 번이 수십 초다. */
  const [draft, setDraft] = useState('');
  const [query, setQuery] = useState('');

  const isS3 = source === 'S3';
  const scope: NasScope = source === 'HQ_NAS' ? 'HQ' : 'SITE';

  const { data: connections } = useBrowsableNasConnections();
  const nasList = useMemo(
    () => nasConnectionsOfScope(connections ?? [], scope),
    [connections, scope],
  );
  /** 주소가 아니라 화면이 들고 있는 값이라, 갈래를 바꾸면 남의 연결이 남을 수 있다 — 여기서 거른다. */
  const activeConnectionId = nasList.some((row) => row.id === connectionId) ? connectionId : null;

  const s3 = useS3Browse(prefix, isS3 && open);
  const nas = useNasBrowse(isS3 ? null : activeConnectionId, path);
  const s3Found = useS3Search(query, '', isS3 && open && query.length > 0);
  const nasFound = useNasSearch(isS3 ? null : activeConnectionId, path, isS3 ? '' : query);

  const searching = query.length > 0;
  const listing: DocumentListing | undefined = searching
    ? (isS3 ? s3Found.data : nasFound.data)
    : (isS3 ? s3.data : nas.data);
  const pending = searching
    ? (isS3 ? s3Found.isPending : nasFound.isPending)
    : (isS3 ? s3.isPending : nas.isPending);

  /**
   * 줄 세우는 기준 — 규칙은 자료실 화면과 <b>같은 것</b>을 쓴다({@link sortEntries}). 「크게 보기」로
   * 넘어가는 길이 있는 이상, 그 순간 순서가 달라지면 사람은 목록이 바뀐 줄 안다.
   *
   * <p>도크에는 목록 머리글이 없어(칸이 좁아 값을 표로 세우지 않는다) 누를 자리가 없었다. 그래서
   * 기준을 <b>한 줄짜리 단추</b>로 따로 세운다 — 좁다고 정렬을 못 하게 두면, 최근 자료를 찾을 때마다
   * 도크를 닫고 화면으로 나가야 한다.
   */
  const [sort, setSort] = useState<DocSort>(DEFAULT_SORT);
  const entries = useMemo(() => sortEntries(listing?.entries ?? [], sort), [listing?.entries, sort]);
  const folders = entries.filter((entry) => entry.directory);
  const files = entries.filter((entry) => !entry.directory);

  /** 갈래를 바꾼다 — 경로·연결·검색을 함께 비운다. 남기면 본사 탭에 현장 폴더가 그대로 뜬다. */
  const pickSource = (next: 'S3' | 'HQ_NAS' | 'SITE_NAS') => {
    setSource(next);
    setPrefix('');
    setPath('');
    setConnectionId(null);
    setDraft('');
    setQuery('');
  };

  const goto = (next: string) => {
    if (isS3) setPrefix(next);
    else setPath(next);
    // 폴더를 옮기면 검색은 끝난다 — 결과 목록 위에서 폴더를 눌렀는데 결과가 남아 있으면
    // 「어느 폴더를 보고 있는 것인지」 가 화면에서 사라진다.
    setDraft('');
    setQuery('');
  };

  const here = isS3 ? prefix : path;
  const crumbs = useMemo(() => buildCrumbs(here, isS3), [here, isS3]);
  /** 한 칸 위. 루트에서는 없다 — 그때는 돌아갈 곳이 없으므로 단추도 세우지 않는다. */
  const parent = crumbs.length > 1 ? crumbs[crumbs.length - 2] : null;

  const downloadOf = (entry: DocumentEntry): string | null =>
    entry.source === 'S3'
      ? entry.downloadUrl
      : activeConnectionId
        ? nasDownloadUrl(activeConnectionId, entry.ref)
        : null;

  /**
   * 파일을 연다 — <b>언제나 새 창</b>이다. 도크에서 같은 창으로 열면 보고 있던 화면이 덮인다.
   * 무엇으로 열지는 화면이 판단하지 않는다({@link viewerKindOf} · {@link officeFormatOf} 한 곳).
   */
  const openFile = (entry: DocumentEntry) => {
    const connection = entry.source === 'NAS' ? activeConnectionId : null;
    if (officeFormatOf(entry.name)) {
      openOfficeWindow(entry.source, entry.ref, connection);
      return;
    }
    window.open(
      docsViewerHref(entry.source, entry.ref, connection),
      `bomi-view-${entry.source}-${entry.ref}`,
      'noopener,noreferrer',
    );
  };

  if (!open) return null;

  return (
    <aside
      className="dock-panel fixed top-14 bottom-0 w-[420px] flex flex-col bg-white no-print"
      style={{ right: offsetRight, borderLeft: '1px solid var(--border)' }}
      aria-label="자료실"
    >
      {/* 버튼은 제목 줄에 둔다(앱 규칙). 「크게 보기」 는 자료실 화면으로 가는 문이다 —
          폴더 도구·AI 학습처럼 도크가 안 맡는 일이 거기 있다. */}
      <div className="flex items-center gap-2 px-3 h-12 flex-shrink-0" style={{ borderBottom: '1px solid var(--border)' }}>
        <svg
          width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
          style={{ color: 'var(--accent)', flexShrink: 0 }}
        >
          <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
        </svg>
        <h2 className="text-sm font-bold truncate min-w-0" style={{ color: 'var(--text-primary)' }}>자료실</h2>

        <div className="ml-auto flex items-center gap-1 flex-shrink-0">
          <button
            onClick={() => navigate(isS3 ? '/docs/s3' : scope === 'HQ' ? '/docs/nas?scope=hq' : '/docs/nas')}
            className="p-1.5 rounded-lg hover:bg-slate-100"
            aria-label="크게 보기"
            title="크게 보기 (자료실 화면)"
          >
            <svg
              width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
              strokeLinecap="round" style={{ color: 'var(--text-secondary)' }}
            >
              <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />
            </svg>
          </button>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100" aria-label="닫기" title="닫기">
            <svg
              width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
              strokeLinecap="round" style={{ color: 'var(--text-secondary)' }}
            >
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      {/* 갈래 — S3 · 본사 NAS · 현장 NAS. 자료실 홈의 카드 셋이 여기서는 한 줄이다. */}
      <div className="flex gap-1 p-1 mx-3 mt-2 rounded-lg flex-shrink-0" style={{ background: 'var(--bg-hover)' }}>
        {SOURCES.map((item) => {
          const active = source === item.value;
          return (
            <button
              key={item.value}
              type="button"
              onClick={() => pickSource(item.value)}
              aria-current={active ? 'page' : undefined}
              className="flex-1 px-2 py-1 rounded-md text-xs whitespace-nowrap"
              style={{
                background: active ? 'white' : 'transparent',
                color: active ? 'var(--accent)' : 'var(--text-muted)',
                fontWeight: active ? 700 : 500,
                boxShadow: active ? '0 1px 2px rgba(0,0,0,0.08)' : undefined,
              }}
            >
              {item.label}
            </button>
          );
        })}
      </div>

      {/* NAS 는 <b>어느 연결인지</b>부터 골라야 목록이 나온다. 좁은 칸이라 판을 열지 않고 고르개 하나로 받는다. */}
      {!isS3 && (
        <div className="px-3 pt-2 flex-shrink-0">
          <select
            className="input-field"
            style={{ fontSize: 12, padding: '5px 8px' }}
            value={activeConnectionId ?? ''}
            aria-label={scope === 'HQ' ? '본사 NAS 고르기' : '현장 NAS 고르기'}
            onChange={(event) => {
              setConnectionId(event.target.value || null);
              setPath('');
              setDraft('');
              setQuery('');
            }}
          >
            <option value="">{nasList.length ? 'NAS 고르기' : '볼 수 있는 NAS 가 없습니다'}</option>
            {nasList.map((row) => (
              <option key={row.id} value={row.id}>{row.name}</option>
            ))}
          </select>
        </div>
      )}

      {/* 이름으로 찾기. <b>Enter 로 보낸다</b> — NAS 검색은 한 번이 수십 초라 타이핑마다 보낼 수 없다. */}
      <div className="px-3 pt-2 flex items-center gap-1 flex-shrink-0">
        <input
          className="input-field flex-1"
          style={{ fontSize: 12, padding: '5px 8px' }}
          placeholder="이름으로 찾기"
          aria-label="파일 이름으로 찾기"
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') setQuery(draft.trim());
          }}
        />
        {searching ? (
          <button
            className="btn-secondary"
            style={COMPACT}
            onClick={() => { setDraft(''); setQuery(''); }}
            title="검색을 끝내고 폴더로 돌아갑니다"
          >
            ✕
          </button>
        ) : (
          <button className="btn-secondary" style={COMPACT} onClick={() => setQuery(draft.trim())}>찾기</button>
        )}
      </div>

      {/* 지금 어느 폴더인가. 검색 중에는 결과가 여러 폴더에 걸쳐 있어 자리를 비운다. */}
      {!searching && (
        <div className="px-3 pt-2 flex flex-wrap items-center gap-1 flex-shrink-0" style={{ fontSize: 11 }}>
          {crumbs.map((crumb, index) => (
            <span key={crumb.path} className="flex items-center gap-1">
              {index > 0 && <span style={{ color: 'var(--text-muted)' }}>/</span>}
              <button
                onClick={() => goto(crumb.path)}
                style={{ color: 'var(--accent)', fontWeight: 600 }}
                className="truncate"
              >
                {crumb.label}
              </button>
            </span>
          ))}
        </div>
      )}

      {/*
        줄 세우기 + 한 칸 위로 — 목록이 있을 때만. 빈 폴더 위에 단추만 남으면 그 자리가 고장으로 읽힌다
        (빈 폴더에서는 아래 가운데의 큰 단추가 그 일을 한다).

        뒤로가기를 이 줄에 <b>상주</b>시키는 이유: 위로 올라가는 길이 빵부스러기의 11px 글씨 하나뿐이라,
        폴더를 한 칸 되짚을 때마다 좁은 글씨를 겨냥해야 했다(사용자 지적 2026-09-07).
      */}
      {entries.length > 0 && (
        <div className="px-3 pt-2 flex items-center gap-1 flex-shrink-0">
          {SORTS.map((item) => (
            <SortChip
              key={item.key}
              label={item.label}
              column={item.key}
              sort={sort}
              onSort={(key) => setSort((prev) => nextSort(prev, key))}
            />
          ))}
          {parent && (
            <button
              type="button"
              onClick={() => goto(parent.path)}
              className="ml-auto px-2 py-0.5 rounded-md hover:bg-slate-50 flex-shrink-0"
              style={{
                fontSize: 11,
                fontWeight: 600,
                color: 'var(--text-secondary)',
                border: '1px solid var(--border)',
              }}
              title={`상위 폴더(${parent.label})로 올라갑니다`}
              aria-label={`상위 폴더 ${parent.label} 로 올라가기`}
            >
              ← 뒤로
            </button>
          )}
        </div>
      )}

      <div className="flex-1 overflow-y-auto px-3 py-2" style={{ fontSize: 12 }}>
        {pending && <p style={MUTED}>불러오는 중입니다…</p>}

        {!pending && listing?.available === false && (
          <p style={{ ...MUTED, color: '#b45309' }}>저장소에 연결하지 못했습니다.</p>
        )}

        {!pending && listing?.available !== false && entries.length === 0 && (
          searching ? (
            <p style={MUTED}>찾은 자료가 없습니다.</p>
          ) : !isS3 && !activeConnectionId ? null : (
            /*
             * 빈 폴더에서는 <b>돌아가는 길</b>이 화면의 주인공이다.
             *
             * 여기서 할 수 있는 일이 그것 하나뿐인데, 나갈 문은 위쪽 빵부스러기의 11px 글씨였다 —
             * 목록이 비어 텅 빈 칸을 놔두고 사람은 좁은 글씨를 겨냥해야 했다. 이 자리는 어차피 비어
             * 있으므로, 유일한 다음 행동을 그 자리에 크게 세운다.
             */
            <div className="flex flex-col items-center justify-center gap-3 py-12 text-center">
              <span aria-hidden style={{ fontSize: 32, opacity: 0.35 }}>📂</span>
              <p style={{ color: 'var(--text-muted)' }}>이 폴더는 비어 있습니다.</p>
              {parent && (
                <button
                  type="button"
                  onClick={() => goto(parent.path)}
                  className="rounded-lg"
                  style={{
                    fontSize: 14,
                    fontWeight: 700,
                    padding: '10px 20px',
                    color: 'var(--accent)',
                    background: 'var(--accent-bg)',
                    border: '1px solid var(--accent)',
                    maxWidth: '100%',
                  }}
                  title={`상위 폴더(${parent.label})로 돌아갑니다`}
                >
                  <span className="truncate">← {parent.label}</span>
                </button>
              )}
            </div>
          )
        )}

        {/* 잘렸다는 것은 말한다 — 감추면 「이게 전부」 로 읽힌다. */}
        {listing?.available && listing.message && (
          <p style={{ ...MUTED, color: '#b45309' }}>{listing.message}</p>
        )}

        {folders.map((entry) => (
          <button
            key={entry.ref}
            className="flex items-center gap-2 w-full text-left rounded px-1.5 py-1.5 hover:bg-slate-50"
            onClick={() => goto(entry.ref)}
          >
            <span aria-hidden>📁</span>
            <span className="truncate" style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{entry.name}</span>
          </button>
        ))}

        {files.map((entry) => (
          <FileRow
            key={entry.ref}
            entry={entry}
            connectionId={entry.source === 'NAS' ? activeConnectionId : null}
            downloadUrl={downloadOf(entry)}
            onOpen={() => openFile(entry)}
          />
        ))}
      </div>
    </aside>
  );
}

const SOURCES: Array<{ value: 'S3' | 'HQ_NAS' | 'SITE_NAS'; label: string }> = [
  { value: 'S3', label: 'S3' },
  { value: 'HQ_NAS', label: '본사 NAS' },
  { value: 'SITE_NAS', label: '현장 NAS' },
];

const COMPACT = { fontSize: 12, padding: '5px 10px' } as const;
const MUTED = { color: 'var(--text-muted)', padding: '8px 2px' } as const;

const SORTS: Array<{ key: SortKey; label: string }> = [
  { key: 'name', label: '이름' },
  { key: 'size', label: '크기' },
  { key: 'modified', label: '수정일' },
];

/**
 * 정렬 단추 하나. 지금 세워진 것만 색이 들어오고 화살표가 붙는다 — 셋 다 화살표를 달아 두면
 * 어느 것이 기준인지 훑어서는 안 보인다. 폴더는 어느 기준에서도 위에 남는다.
 */
function SortChip({
  label,
  column,
  sort,
  onSort,
}: {
  label: string;
  column: SortKey;
  sort: DocSort;
  onSort: (key: SortKey) => void;
}) {
  const active = sort.key === column;
  return (
    <button
      type="button"
      onClick={() => onSort(column)}
      aria-label={`${label}순 정렬${active ? (sort.dir === 'asc' ? ' — 오름차순' : ' — 내림차순') : ''}`}
      className="px-2 py-0.5 rounded-md"
      style={{
        fontSize: 11,
        fontWeight: active ? 700 : 500,
        color: active ? 'var(--accent)' : 'var(--text-muted)',
        background: active ? 'var(--accent-bg)' : 'transparent',
        border: `1px solid ${active ? 'var(--accent)' : 'var(--border)'}`,
      }}
      title={`${label}순으로 이 폴더를 다시 세웁니다. 다시 누르면 반대 방향입니다.`}
    >
      {label}
      {active && <span aria-hidden="true">{sort.dir === 'asc' ? ' ▲' : ' ▼'}</span>}
    </button>
  );
}

/**
 * 파일 한 줄 — <b>끌 수 있는 줄</b>이다.
 *
 * <p>줄 전체가 끌기 대상이다. 손잡이를 따로 두면 그 작은 점을 찾아 눌러야 하고, 좁은 칸에서
 * 그것은 사실상 «못 끄는 것» 이 된다. 대신 [열기]·[받기] 는 버튼이라 끌기와 헷갈리지 않는다.
 */
function FileRow({
  entry,
  connectionId,
  downloadUrl,
  onOpen,
}: {
  entry: DocumentEntry;
  connectionId: string | null;
  downloadUrl: string | null;
  onOpen: () => void;
}) {
  const openable = !!viewerKindOf(entry.name) || !!officeFormatOf(entry.name);

  return (
    <div
      draggable
      onDragStart={(event) =>
        setDocDrag(event.dataTransfer, {
          source: entry.source,
          ref: entry.ref,
          name: entry.name,
          connectionId,
          categoryId: entry.categoryId,
        })
      }
      className="flex items-center gap-2 rounded px-1.5 py-1.5 hover:bg-slate-50"
      style={{ cursor: 'grab' }}
      title={entry.ref}
    >
      <span aria-hidden>📄</span>
      <span className="flex-1 min-w-0">
        <span className="block truncate" style={{ color: 'var(--text-primary)' }}>{entry.name}</span>
        {/* 크기와 수정일 — 수정일순으로 세울 수 있게 된 이상, 그 값이 화면에 없으면 세운 결과를
            확인할 길이 없다. 둘 중 없는 값은 자리째 빠진다(빈 가운뎃점을 남기지 않는다). */}
        <span className="block" style={{ fontSize: 11, color: 'var(--text-muted)' }}>
          {[sizeOf(entry.size), dateOf(entry.modifiedAt)].filter(Boolean).join(' · ')}
        </span>
      </span>

      {openable && (
        <button
          onClick={onOpen}
          className="btn-secondary flex-shrink-0"
          style={{ fontSize: 11, padding: '3px 8px' }}
          title="새 창으로 엽니다"
        >
          열기
        </button>
      )}
      {downloadUrl && (
        <a
          href={downloadUrl}
          download={entry.name}
          className="btn-secondary flex-shrink-0"
          style={{ fontSize: 11, padding: '3px 8px' }}
          // 끌기는 <b>줄</b>의 일이다 — 링크는 자기 주소를 끌어 보내려 해서, 여기서 막지 않으면
          // 받는 쪽에 우리 규약 대신 URL 한 줄이 떨어진다.
          draggable={false}
        >
          받기
        </a>
      )}
    </div>
  );
}

/** 값이 없거나 깨졌으면 빈 문자열 — 좁은 칸에 '—' 를 세우느니 자리를 비운다. */
function dateOf(value: string | null): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${String(date.getFullYear()).slice(2)}.${pad(date.getMonth() + 1)}.${pad(date.getDate())}`;
}

function sizeOf(bytes: number | null): string {
  if (bytes == null) return '';
  if (bytes < 1024) return `${bytes}B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)}KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)}MB`;
}

/** 루트부터 지금까지의 조각. S3 는 끝에 `/` 가 붙고 NAS 는 안 붙는다(표기 규칙이 다르다). */
function buildCrumbs(here: string, isS3: boolean): Array<{ label: string; path: string }> {
  const crumbs = [{ label: '자료실', path: '' }];
  const segments = here.split('/').filter(Boolean);
  let acc = '';
  for (const segment of segments) {
    acc = isS3 ? `${acc}${segment}/` : acc ? `${acc}/${segment}` : segment;
    crumbs.push({ label: segment, path: acc });
  }
  return crumbs;
}
